import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CssBaseline, Box, CircularProgress, Typography } from '@mui/material';
import { Provider } from 'react-redux';
import { store } from './store/store';
import { ThemeProvider } from './contexts/ThemeContext';
import { RtlGate } from './contexts/RtlGate';
import ThemeApplier from './components/ThemeApplier';
import Dashboard from './pages/Dashboard/Dashboard';
import LoginPage from './pages/Login/LoginPage';
// Import utility to fix admin role (makes it available in browser console)
import './utils/fixAdminRole';
import UsersPage from './pages/Users/UsersPage';
import MenuPage from './pages/Menu/MenuPage';
import OrdersPage from './pages/Orders/OrdersPage';
import ReportsPage from './pages/Reports/ReportsPage';
import MarketingPage from './pages/Marketing/MarketingPage';
import AuditLogPage from './pages/AuditLog/AuditLogPage';
import ShiftsPage from './pages/Shifts/ShiftsPage';
import SettingsPage from './pages/Settings/SettingsPage';
import BranchesPage from './pages/Branches/BranchesPage';
import DriversPage from './pages/Drivers/DriversPage';
import ThemePage from './pages/Theme/ThemePage';
import StaffMonitoringPage from './pages/Monitoring/StaffMonitoringPage';
import AdminDailyClosingReportsPage from './pages/AdminDailyClosingReports/AdminDailyClosingReportsPage';
import AddDriverPage from './pages/Drivers/AddDriverPage';
import CustomersPage from './pages/Customers/CustomersPage';
import TablesPage from './pages/Tables/TablesPage';
import PrinterRoutingPage from './pages/PrinterRouting/PrinterRoutingPage';
import DeliveryZonesPage from './pages/DeliveryZones/DeliveryZonesPage';
import DiscountMonitoringPage from './pages/Discounts/DiscountMonitoringPage';
import QrCodesPage from './pages/QrCodes/QrCodesPage';
import ModifiersPage from './pages/Modifiers/ModifiersPage';
import { SessionManager } from './utils/sessionManager';
import { supabase } from './services/supabaseClient';
import { ErrorBoundary } from './components/ErrorBoundary';

/**
 * ProtectedRoute — blocks access unless user is authenticated.
 * Auth source of truth is Supabase (Firebase is not configured for Saheeh
 * yet). SessionManager is the local-storage mirror used by the rest of the
 * admin app for read-only access to the current user id / email.
 */
const ProtectedRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { t } = useTranslation();
  const [authState, setAuthState] = useState<'loading' | 'authenticated' | 'unauthenticated'>('loading');

  useEffect(() => {
    let mounted = true;

    // Hard safety: if anything below stalls (corrupted localStorage, network
    // outage, etc.), drop the user at /login after 4 s rather than hanging
    // on a black screen. Better to re-login than to stare at nothing.
    const stallTimer = setTimeout(() => {
      if (mounted) {
        console.warn('[auth] getSession() did not resolve in 4s — falling back to /login');
        setAuthState('unauthenticated');
      }
    }, 4000);

    // First pass: check Supabase for an active session.
    supabase.auth.getSession()
      .then(({ data }) => {
        if (!mounted) return;
        clearTimeout(stallTimer);
        if (data.session?.user && SessionManager.isLoggedIn()) {
          setAuthState('authenticated');
        } else if (data.session?.user && !SessionManager.isLoggedIn()) {
          // Rehydrate the SessionManager from the Supabase session so other
          // parts of the admin app see a valid local session.
          SessionManager.saveSession(
            data.session.access_token,
            '',
            {
              email: data.session.user.email,
              role: 'admin',
              uid: data.session.user.id,
            },
          );
          setAuthState('authenticated');
        } else {
          SessionManager.clearSession();
          setAuthState('unauthenticated');
        }
      })
      .catch((err) => {
        console.error('[auth] getSession() rejected:', err);
        if (!mounted) return;
        clearTimeout(stallTimer);
        SessionManager.clearSession();
        setAuthState('unauthenticated');
      });

    // Subscribe to subsequent auth changes (sign-in / sign-out / token refresh).
    const { data: subscription } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      if (session?.user) {
        if (!SessionManager.isLoggedIn()) {
          SessionManager.saveSession(
            session.access_token,
            '',
            { email: session.user.email, role: 'admin', uid: session.user.id },
          );
        }
        setAuthState('authenticated');
      } else {
        SessionManager.clearSession();
        setAuthState('unauthenticated');
      }
    });

    return () => {
      mounted = false;
      subscription.subscription.unsubscribe();
    };
  }, []);

  if (authState === 'loading') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: 2 }}>
        <CircularProgress size={32} />
        <Typography variant="body2" sx={{ color: '#718096' }}>{t('login.verifyingAuth')}</Typography>
      </Box>
    );
  }

  if (authState === 'unauthenticated') {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
    <Provider store={store}>
      <RtlGate>
      <ThemeProvider>
        <ThemeApplier />
        <CssBaseline />
        <Router>
          <Routes>
            {/* Public route — login page */}
            <Route path="/login" element={<LoginPage />} />

            {/* All other routes require authentication */}
            <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/users" element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />
            <Route path="/menu" element={<ProtectedRoute><MenuPage /></ProtectedRoute>} />
            <Route path="/orders" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
            <Route path="/monitoring" element={<ProtectedRoute><StaffMonitoringPage /></ProtectedRoute>} />
            <Route path="/admin-daily-closing" element={<ProtectedRoute><AdminDailyClosingReportsPage /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
            <Route path="/marketing" element={<ProtectedRoute><MarketingPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/branches" element={<ProtectedRoute><BranchesPage /></ProtectedRoute>} />
            <Route path="/drivers" element={<ProtectedRoute><DriversPage /></ProtectedRoute>} />
            <Route path="/drivers/add" element={<ProtectedRoute><AddDriverPage /></ProtectedRoute>} />
            <Route path="/customers" element={<ProtectedRoute><CustomersPage /></ProtectedRoute>} />
            <Route path="/theme" element={<ProtectedRoute><ThemePage /></ProtectedRoute>} />
            <Route path="/tables" element={<ProtectedRoute><TablesPage /></ProtectedRoute>} />
            <Route path="/printer-routing" element={<ProtectedRoute><PrinterRoutingPage /></ProtectedRoute>} />
            <Route path="/delivery-zones" element={<ProtectedRoute><DeliveryZonesPage /></ProtectedRoute>} />
            <Route path="/discounts" element={<ProtectedRoute><DiscountMonitoringPage /></ProtectedRoute>} />
            <Route path="/audit-log" element={<ProtectedRoute><AuditLogPage /></ProtectedRoute>} />
            <Route path="/shifts" element={<ProtectedRoute><ShiftsPage /></ProtectedRoute>} />
            <Route path="/qr-codes" element={<ProtectedRoute><QrCodesPage /></ProtectedRoute>} />
            <Route path="/modifiers" element={<ProtectedRoute><ModifiersPage /></ProtectedRoute>} />

            {/* Catch-all redirects to login if not authenticated */}
            <Route path="*" element={<Navigate to="/login" />} />
          </Routes>
        </Router>
      </ThemeProvider>
      </RtlGate>
    </Provider>
    </ErrorBoundary>
  );
};

export default App;

